import { Box, styled } from "@mui/material";
import type { PointerEvent as ReactPointerEvent } from "react";
import { useCallback, useEffect, useMemo, useRef, useSyncExternalStore } from "react";
import { createPortal } from "react-dom";

import { ELEMENT_IDS } from "features/bookmarks/utils";
import { useNonMarkerUIIsHidden } from "features/markers/markerCreationUtils";

import { DOCK_EDGE_MARGIN, DOCK_LEFT, DOCK_MAX_WIDTH, DOCK_MIN_WIDTH, DOCK_Z_INDEX } from "./constants";
import { type DockSlotMeasurement, useDockDragController } from "./dockDragContext";
import { useDockLayoutContext } from "./dockLayoutContext";

const Container = styled(Box)`
    position: absolute;
    left: ${DOCK_LEFT}px;
    top: ${DOCK_EDGE_MARGIN}px;
    bottom: ${DOCK_EDGE_MARGIN}px;
    z-index: ${DOCK_Z_INDEX};
    pointer-events: none;
    border-radius: 12px;
    transition: background-color 150ms ease;
`;

/** Gray rectangle stand-in for a panel mid-drag-out, or for an incoming drag-in target. */
const PlaceholderSlot = styled(Box)`
    pointer-events: none;
    position: absolute;
    left: 0;
    right: 0;
    background: rgba(0, 0, 0, 0.12);
    border-radius: 8px;
`;

const WidthResizeHandle = styled(Box)`
    position: absolute;
    top: 0;
    bottom: 0;
    right: -4px;
    width: 8px;
    cursor: ew-resize;
    z-index: 2;
    touch-action: none;
    pointer-events: auto;
`;

/**
 * Left-edge dock for windows with `docked: true`. Portals into the render
 * container and publishes zone + slot measurements to the drag controller.
 */
export function DockManager() {
    const nonMarkerUIIsHidden = useNonMarkerUIIsHidden();
    const controller = useDockDragController();
    const { isDragging } = controller.state;
    const { setZoneRef, slots, zoneRect } = useDockLayoutContext();

    const liveWidth = useSyncExternalStore(controller.subscribeLiveWidth, controller.getLiveWidth, () => null);
    const effectiveWidth = liveWidth?.width ?? controller.dockWidth;

    const widthDragCleanupRef = useRef<(() => void) | null>(null);

    const handleWidthResizePointerDown = useCallback(
        (e: ReactPointerEvent<HTMLDivElement>) => {
            widthDragCleanupRef.current?.();

            const startX = e.clientX;
            const startW = controller.dockWidth;
            const target = e.currentTarget;
            const pointerId = e.pointerId;
            target.setPointerCapture(pointerId);

            const onMove = (ev: PointerEvent) => {
                const delta = ev.clientX - startX;
                const next = Math.min(DOCK_MAX_WIDTH, Math.max(DOCK_MIN_WIDTH, startW + delta));
                controller.setLiveWidth({ width: next });
            };
            const cleanup = () => {
                target.removeEventListener("pointermove", onMove);
                target.removeEventListener("pointerup", onUp);
                target.removeEventListener("pointercancel", onUp);
                if (target.hasPointerCapture(pointerId)) {
                    target.releasePointerCapture(pointerId);
                }
                const live = controller.getLiveWidth();
                if (live) {
                    controller.setDockWidth(live.width);
                }
                controller.setLiveWidth(null);
                if (widthDragCleanupRef.current === cleanup) {
                    widthDragCleanupRef.current = null;
                }
            };
            const onUp = () => cleanup();

            target.addEventListener("pointermove", onMove);
            target.addEventListener("pointerup", onUp);
            target.addEventListener("pointercancel", onUp);
            widthDragCleanupRef.current = cleanup;
        },
        [controller],
    );

    useEffect(() => {
        return () => {
            widthDragCleanupRef.current?.();
            controller.setLiveWidth(null);
        };
    }, [controller]);

    // Hit-test on real slots only — keeps `insertionIndex` in real-slot space
    // and stops it from oscillating as the phantom moves between indices.
    const slotMeasurements = useMemo<DockSlotMeasurement[]>(() => {
        return slots
            .filter((slot) => !slot.isPlaceholder)
            .map((slot) => ({ id: slot.id, top: slot.top, height: slot.height }));
    }, [slots]);

    useEffect(() => {
        controller.registerZone(zoneRect);
    }, [controller, zoneRect]);

    useEffect(() => {
        controller.registerSlots(slotMeasurements);
    }, [controller, slotMeasurements]);

    const container = document.getElementById(ELEMENT_IDS.renderContainer);
    if (!container) {
        return null;
    }
    // Stay mounted during any drag so empty docks still receive drop-ins and
    // drag-outs of the last docked panel keep rendering the placeholder.
    if (slots.length === 0 && !isDragging) {
        return null;
    }

    return createPortal(
        <Container
            ref={setZoneRef}
            data-testid="dock-manager"
            style={{
                width: effectiveWidth,
                visibility: nonMarkerUIIsHidden ? "hidden" : undefined,
            }}
        >
            {slots
                .filter((slot) => slot.isPlaceholder)
                .map((slot) => (
                    <PlaceholderSlot
                        key={slot.id}
                        data-testid={`dock-placeholder-${slot.id}`}
                        style={{ top: slot.top, height: slot.height }}
                    />
                ))}
            <WidthResizeHandle data-testid="dock-width-resize-handle" onPointerDown={handleWidthResizePointerDown} />
        </Container>,
        container,
    );
}
